import React, { useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Hyperspeed from './Hyperspeed';
import ParticleText from './ParticleText';
import CircularGallery from './CircularGallery';
import InfiniteMenu from './InfiniteMenu';
import { hyperspeedPresets } from '../utils/hyperspeedPresets';
import './HomePage.css';

// Import images
import cafeImg from '../images/cafe.jpg';
import qrkodImg from '../images/qrkod.png';
import b2bImg from '../images/b2b.jpg';
import eticaretImg from '../images/eticaret.jpg';
import n8nImg from '../images/n8n.webp';

gsap.registerPlugin(ScrollTrigger);

const HomePage = () => {
  const servicesRef = useRef(null);
  const projectsRef = useRef(null);
  const cardsRef = useRef([]);
  const projectCardsRef = useRef([]);
  const cardsContainerRef = useRef(null);
  const statisticsRef = useRef(null);
  const statNumbersRef = useRef([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [showScrollTop, setShowScrollTop] = React.useState(false);
  const [formData, setFormData] = React.useState({
    ad: '',
    telefon: '',
    konu: '',
    mesaj: ''
  });
  const [formStatus, setFormStatus] = React.useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  useEffect(() => {
    // İlk yüklemede scroll pozisyonunu 0'a ayarla
    window.scrollTo(0, 0);

    // Scroll top button visibility
    const handleScroll = () => {
      if (window.scrollY > 500) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    const scrollListener = handleScroll;

    // Smooth scroll için link click handler
    const handleLinkClick = (e) => {
      const href = e.target.getAttribute('href');
      if (href && href.startsWith('#')) {
        e.preventDefault();
        const targetId = href.substring(1);
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };

    const navLinks = document.querySelectorAll('.nav-menu a');
    navLinks.forEach(link => {
      link.addEventListener('click', handleLinkClick);
    });

    // Cleanup function için navLinks'i sakla
    const cleanupNavLinks = () => {
      navLinks.forEach(link => {
        link.removeEventListener('click', handleLinkClick);
      });
    };

    // Header animasyonu - başlangıç değerlerini ayarla
    gsap.set('.main-header', {
      y: -100,
      opacity: 0
    });

    gsap.to('.main-header', {
      y: 0,
      opacity: 1,
      duration: 1,
      ease: 'power3.out',
      delay: 0.3
    });

    // Main content animasyonu - başlangıç değerlerini ayarla
    gsap.set('.main-content', {
      opacity: 0,
      y: 30
    });

    gsap.to('.main-content', {
      opacity: 1,
      y: 0,
      duration: 1.2,
      ease: 'power3.out',
      delay: 0.5
    });

    // Parallax scroll efekti için scroll listener
    const handleParallax = () => {
      const scrolled = window.pageYOffset;
      const parallaxElements = document.querySelectorAll('.hero-background-elements .gradient-orb');
      parallaxElements.forEach((element, index) => {
        const speed = 0.2 + (index * 0.1);
        const yPos = -(scrolled * speed);
        gsap.set(element, {
          y: yPos
        });
      });
    };

    window.addEventListener('scroll', handleParallax);
    
    // Cleanup için handleParallax'ı sakla
    const parallaxHandler = handleParallax;

    // Services section animasyonu
    if (servicesRef.current) {
      gsap.set(servicesRef.current, {
        opacity: 1,
        y: 0
      });

      gsap.from(servicesRef.current, {
        opacity: 0,
        y: 50,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: servicesRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none'
        }
      });
    }

    // Card animasyonları
    const isMobile = window.innerWidth <= 768;

    if (isMobile && servicesRef.current && cardsContainerRef.current) {
      // Mobil için horizontal scroll
      const cards = cardsRef.current.filter(card => card !== null);
      const cardCount = cards.length;

      if (cardCount > 0) {
        // Container'ı görünür yap
        gsap.set(cardsContainerRef.current, {
          opacity: 1,
          visibility: 'visible'
        });

        // Her kartı görünür yap
        cards.forEach(card => {
          gsap.set(card, {
            opacity: 1,
            visibility: 'visible'
          });
        });

        // Kart genişliği ve boşluk
        const cardWidth = window.innerWidth * 0.85;
        const gap = 20;

        // Toplam scroll mesafesi: her kart için bir viewport height
        const scrollDistance = window.innerHeight * cardCount;

        // ScrollTrigger ile pin ve horizontal scroll
        ScrollTrigger.create({
          trigger: servicesRef.current,
          start: 'top top',
          end: `+=${scrollDistance}`,
          pin: true,
          scrub: 1,
          onUpdate: (self) => {
            const progress = self.progress;

            // Her kartın x pozisyonunu hesapla
            // İlk kart ortada başlar, scroll ile sola kayar
            const totalWidth = (cardWidth + gap) * cardCount;
            const maxScroll = totalWidth - window.innerWidth / 2 - cardWidth / 2;
            const scrollAmount = progress * maxScroll;

            gsap.set(cardsContainerRef.current, {
              x: -scrollAmount
            });
          }
        });
      }
    } else {
      // Desktop için basit animasyon
      cardsRef.current.forEach((card, index) => {
        if (card) {
          gsap.set(card, {
            opacity: 1,
            y: 0,
            scale: 1
          });

          gsap.from(card, {
            y: 60,
            opacity: 0,
            scale: 0.95,
            duration: 0.7,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: card,
              start: 'top 85%',
              toggleActions: 'play none none none'
            },
            delay: index * 0.1
          });
        }
      });
    }

    // Statistics section animasyonu
    if (statisticsRef.current) {
      gsap.set(statisticsRef.current, {
        opacity: 1,
        y: 0
      });

      gsap.from(statisticsRef.current, {
        opacity: 0,
        y: 50,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: statisticsRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none',
          onEnter: () => {
            // Sayıları animasyonla artır
            statNumbersRef.current.forEach((element, index) => {
              if (element) {
                const target = parseInt(element.getAttribute('data-target'));
                const suffix = element.textContent.replace(/[0-9]/g, '');
                
                gsap.to({ value: 0 }, {
                  value: target,
                  duration: 2,
                  ease: 'power2.out',
                  onUpdate: function() {
                    element.textContent = Math.floor(this.targets()[0].value) + suffix;
                  }
                });
              }
            });
          }
        }
      });
    }

    // Projects section animasyonu
    if (projectsRef.current) {
      gsap.set(projectsRef.current, {
        opacity: 1,
        y: 0
      });

      gsap.from(projectsRef.current, {
        opacity: 0,
        y: 50,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: projectsRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none'
        }
      });

    }

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      cleanupNavLinks();
      window.removeEventListener('scroll', scrollListener);
      window.removeEventListener('scroll', parallaxHandler);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const response = await fetch('/send_email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.status === 'success') {
        setFormStatus({ type: 'success', message: data.message });
        setFormData({ ad: '', telefon: '', konu: '', mesaj: '' });
      } else {
        setFormStatus({ type: 'error', message: data.message });
      }
    } catch (error) {
      setFormStatus({ 
        type: 'error', 
        message: 'Bir hata oluştu. Lütfen daha sonra tekrar deneyin.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="home-page">
      

      <div className="hyperspeed-wrapper">
        <Hyperspeed
          effectOptions={hyperspeedPresets.one}
        />
      </div>

      {/* Header */}
      <header className="main-header">
        <div className="header-container">
          <Link to="/" className="header-logo">
            <img src="/images/logo.png" alt="Logo" />
            <div className="header-logo-text">
              <span className="logo-main">DİGİTALENTRA</span>
              <span className="logo-sub">Software</span>
            </div>
          </Link>
          
          <button 
            className="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menu"
          >
            <span className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>

          <nav className="nav-menu desktop-only">
            <ul>
              <li><a href="#home">Anasayfa</a></li>
              <li><Link to="/hizmetlerimiz">Hizmetlerimiz</Link></li>
              <li><Link to="/projelerimiz">Projelerimiz</Link></li>
              <li><Link to="/iletisim">İletişim</Link></li>
            </ul>
          </nav>
        </div>
      </header>

      {/* Mobile Menu - Outside header */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-fullscreen">
          <button 
            className="mobile-menu-close"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Close Menu"
          >
            <span className="close-icon">
              <span></span>
              <span></span>
            </span>
          </button>
          <nav className="mobile-nav">
            <ul>
              <li><a href="#home" onClick={() => setIsMobileMenuOpen(false)}>Anasayfa</a></li>
              <li><Link to="/hizmetlerimiz" onClick={() => setIsMobileMenuOpen(false)}>Hizmetlerimiz</Link></li>
              <li><Link to="/projelerimiz" onClick={() => setIsMobileMenuOpen(false)}>Projelerimiz</Link></li>
              <li><Link to="/iletisim" onClick={() => setIsMobileMenuOpen(false)}>İletişim</Link></li>
            </ul>
          </nav>
        </div>
      )}

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-background-elements">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>
        <div className="main-content">
          <h1 className="main-title">
            <span className="text-reveal">Modern web siteleri, mobil uygulamalar ve yazılım çözümleri ile</span>
            <br />
            <span className="text-reveal" style={{ animationDelay: '0.2s' }}>işletmenizi dijital dünyaya taşıyoruz</span>
          </h1>

          <div className="button-group">
            <a href="#contact" className="btn btn-primary">
              <span>İletişime Geçin</span>
              <div className="btn-shine"></div>
            </a>
            <a href="#services" className="btn btn-secondary">
              <span>Hizmetlerimiz</span>
              <div className="btn-shine"></div>
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" ref={servicesRef} className="services-section">
        <div className="services-container">
          <div className="services-header">
            <h2 className="services-title">Hizmetlerimiz</h2>
            <p className="services-subtitle">Dijital dönüşümünüz için kapsamlı çözümler</p>
          </div>

          <div ref={cardsContainerRef} className="services-grid">
            {[
              {
                icon: '🌐',
                title: 'Web Geliştirme',
                description: 'Modern ve responsive web siteleri'
              },
              {
                icon: '📱',
                title: 'Mobil Uygulama',
                description: 'iOS ve Android uygulamaları'
              },
              {
                icon: '⚡',
                title: 'Backend Geliştirme',
                description: 'Güvenli ve ölçeklenebilir sistemler'
              },
              {
                icon: '☁️',
                title: 'Bulut Çözümleri',
                description: 'AWS, Azure ve Google Cloud'
              },
              {
                icon: '🤖',
                title: 'AI & ML',
                description: 'Yapay zeka çözümleri'
              },
              {
                icon: '🎨',
                title: 'UI/UX Design',
                description: 'Kullanıcı deneyimi tasarımı'
              }
            ].map((service, index) => (
              <div
                key={index}
                ref={(el) => cardsRef.current[index] = el}
                className="service-card"
              >
                <div className="service-icon">{service.icon}</div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Particle Text Animation */}
      <ParticleText />

      {/* Statistics Section */}
      <section id="statistics" ref={statisticsRef} className="statistics-section">
        <div className="statistics-container">
          <div className="statistics-header">
            <h2 className="statistics-title">Rakamlarla Digitalentra</h2>
            <p className="statistics-subtitle">Başarılarımızın sayılarla ifadesi</p>
          </div>
          <div className="statistics-grid">
            {[
              { number: 60, suffix: '+', label: 'Tamamlanan Proje', icon: '🎯' },
              { number: 50, suffix: '+', label: 'Mutlu Müşteri', icon: '😊' },
              { number: 5, suffix: '+', label: 'Yıllık Deneyim', icon: '⭐' },
              { number: 98, suffix: '%', label: 'Müşteri Memnuniyeti', icon: '😊' }
            ].map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-icon">{stat.icon}</div>
                <div 
                  className="stat-number" 
                  data-target={stat.number}
                  ref={(el) => statNumbersRef.current[index] = el}
                >
                  0{stat.suffix}
                </div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Infinite Menu Section */}
      <section id="infinite-menu" className="infinite-menu-section">
        <div className="infinite-menu-container">
          <div className="infinite-menu-header">
            <h2 className="infinite-menu-title">Referanslarımız</h2>
            <p className="infinite-menu-subtitle">
              Güvenilir çözümlerimizle büyük markalara hizmet veriyoruz. Başarılı iş ortaklıklarımız ve memnun müşterilerimiz, kalitemizin en büyük kanıtı.
            </p>
          </div>

          <div className="infinite-menu-wrapper">
            <div style={{ height: '600px', position: 'relative' }}>
              <InfiniteMenu
                items={useMemo(() => [
                  {
                    image: '/images/cocacola.jpg',
                    link: '#',
                    title: 'Coca-Cola Projesi',
                    description: 'Coca-Cola için özel geliştirilmiş dijital çözümler'
                  },
                  {
                    image: '/images/corlutaksi.png',
                    link: '#',
                    title: 'Çorlu Taksi',
                    description: 'Çorlu Taksi için geliştirilmiş dijital çözümler'
                  },
                  {
                    image: '/images/elmasimit.png',
                    link: '#',
                    title: 'Elmas İmit',
                    description: 'Elmas İmit için geliştirilmiş dijital çözümler'
                  },
                  {
                    image: '/images/samdan.png',
                    link: '#',
                    title: 'Şamdan Yapı',
                    description: 'Şamdan için geliştirilmiş web sitesi'
                  },
                  {
                    image: '/images/postaihale.png',
                    link: '#',
                    title: 'Posta İhale',
                    description: 'Posta İhale sistemi için geliştirilmiş dijital platform'
                  }
                ], [])}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials-section">
        <div className="testimonials-container">
          <div className="testimonials-header">
            <h2 className="testimonials-title">Müşteri Yorumları</h2>
            <p className="testimonials-subtitle">Bizi tercih eden müşterilerimizin görüşleri</p>
          </div>
          <div className="testimonials-grid">
            {[
              {
                name: 'A***T Y****Z',
                role: 'Kafe İşletmecisi',
                image: '👨‍💼',
                text: 'Kafe otomasyon sistemimiz sayesinde işlerimiz çok daha düzenli hale geldi. Sipariş takibi ve stok yönetimi artık çok kolay. Teşekkürler Digitalentra!',
                rating: 5
              },
              {
                name: 'A**E D***R',
                role: 'E-Ticaret Girişimcisi',
                image: '👩‍💼',
                text: 'E-ticaret sitemiz çok profesyonel ve hızlı. Müşteri memnuniyetimiz arttı, satışlarımız %40 yükseldi. Kesinlikle tavsiye ederim.',
                rating: 5
              },
              {
                name: 'M****T K**A',
                role: 'B2B Ticaret Müdürü',
                image: '👨‍💻',
                text: 'B2B platformumuz sayesinde bayi yönetimi çok kolaylaştı. Sipariş süreçleri otomatikleşti ve iş verimliliğimiz arttı. Harika bir çözüm!',
                rating: 5
              }
            ].map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-header">
                  <div className="testimonial-avatar">{testimonial.image}</div>
                  <div className="testimonial-info">
                    <h3 className="testimonial-name">{testimonial.name}</h3>
                    <p className="testimonial-role">{testimonial.role}</p>
                  </div>
                </div>
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="star">⭐</span>
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" ref={projectsRef} className="projects-section">
        <div className="projects-container">
          <div className="projects-header">
            <h2 className="projects-title">Projelerimiz</h2>
            <p className="projects-subtitle">
              Başarıyla tamamladığımız projelerden örnekler
            </p>
          </div>

          <div className="projects-circular-gallery-wrapper" style={{ height: '600px', width: '100%' }}>
            <CircularGallery
              items={useMemo(() => [
                {
                  image: '/images/cafe.jpg',
                  text: 'Kafe Otomasyon Sistemi'
                },
                {
                  image: '/images/qrkod.png',
                  text: 'QR Menü Çözümleri'
                },
                {
                  image: '/images/b2b.jpg',
                  text: 'B2B Sistemi'
                },
                {
                  image: '/images/eticaret.jpg',
                  text: 'E-Ticaret Siteleri'
                },
                {
                  image: '/images/n8n.webp',
                  text: 'n8n Otomasyon Çözümleri'
                }
              ], [])}
              bend={3}
              textColor="#ffffff"
              borderRadius={0.05}
              font="bold 30px Figtree"
              scrollSpeed={2}
              scrollEase={0.05}
            />
          </div>
        </div>
      </section>
      
      <section id="technologies" className="technologies-section">
        <div className="technologies-container">
          <div className="technologies-header">
            <h2 className="technologies-title">Kullandığımız Teknolojiler</h2>
            <p className="technologies-subtitle">Modern ve ölçeklenebilir çözümler için tercih ettiğimiz teknolojiler</p>
          </div>

          <div className="tech-slider">
            <div className="tech-track">
              {[
                { name: 'React', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg' },
                { name: 'Vue', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vuejs/vuejs-original.svg' },
                { name: 'Flutter', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flutter/flutter-original.svg' },
                { name: 'JavaScript', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
                { name: 'C#', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/csharp/csharp-original.svg' },
                { name: 'PHP', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/php/php-original.svg' },
                { name: 'PostgreSQL', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg' },
                { name: 'MongoDB', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg' },
                { name: 'Firebase', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg' },
                { name: 'Google Cloud', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/googlecloud/googlecloud-original.svg' },
                { name: 'AWS', src: 'https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg' }
              ].concat([
                { name: 'React', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg' },
                { name: 'Vue', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vuejs/vuejs-original.svg' },
                { name: 'Flutter', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flutter/flutter-original.svg' },
                { name: 'JavaScript', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
                { name: 'C#', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/csharp/csharp-original.svg' },
                { name: 'PHP', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/php/php-original.svg' },
                { name: 'PostgreSQL', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg' },
                { name: 'MongoDB', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg' },
                { name: 'Firebase', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg' },
                { name: 'Google Cloud', src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/googlecloud/googlecloud-original.svg' },
                { name: 'AWS', src: 'https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg' }
              ]).map((t, i) => (
                <div className="tech-item" key={i}>
                  <img className="tech-logo" src={t.src} alt={t.name} loading="lazy" />
                  <span className="tech-name">{t.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="contact-container">
          <div className="contact-header">
            <h2 className="contact-title">İletişime Geçin</h2>
            <p className="contact-subtitle">Projeleriniz için bizimle iletişime geçin</p>
          </div>

          <form className="contact-form" onSubmit={handleFormSubmit}>
            {formStatus.message && (
              <div className={`form-message form-message-${formStatus.type}`}>
                {formStatus.message}
              </div>
            )}

            <div className="form-group">
              <input 
                type="text" 
                name="ad"
                className="form-input" 
                placeholder="Adınız"
                value={formData.ad}
                onChange={handleFormChange}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <input 
                type="tel" 
                name="telefon"
                className="form-input" 
                placeholder="Telefon Numaranız"
                value={formData.telefon}
                onChange={handleFormChange}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <input 
                type="text" 
                name="konu"
                className="form-input" 
                placeholder="Konu"
                value={formData.konu}
                onChange={handleFormChange}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <textarea 
                name="mesaj"
                className="form-input form-textarea" 
                placeholder="Mesajınız"
                rows="6"
                value={formData.mesaj}
                onChange={handleFormChange}
                required
                disabled={isSubmitting}
              ></textarea>
            </div>

            <button type="submit" className="form-submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'GÖNDERİLİYOR...' : 'GÖNDER'}
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <img src="/images/logo.png" alt="Logo" />
                <span>DİGİTALENTRA</span>
              </div>
              <p className="footer-description">
                Modern web siteleri, mobil uygulamalar ve yazılım çözümleri ile işletmenizi dijital dünyaya taşıyoruz.
              </p>
            </div>

            <div className="footer-section">
              <h3 className="footer-title">Hızlı Linkler</h3>
              <ul className="footer-links">
                <li><a href="#home">Anasayfa</a></li>
                <li><a href="#services">Hizmetler</a></li>
                <li><a href="#projects">Projelerimiz</a></li>
                <li><a href="#technologies">Teknolojiler</a></li>
                <li><a href="#contact">İletişim</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h3 className="footer-title">Hizmetlerimiz</h3>
              <ul className="footer-links">
                <li><a href="#services">Web Geliştirme</a></li>
                <li><a href="#services">Mobil Uygulama</a></li>
                <li><a href="#services">Backend Geliştirme</a></li>
                <li><a href="#services">Bulut Çözümleri</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h3 className="footer-title">İletişim</h3>
              <ul className="footer-contact">
                <li>📧 <a href="mailto:info@digitalentrasoftware.com">info@digitalentrasoftware.com</a></li>
                <li>📱 <a href="tel:+905318287339">0531 828 7339</a></li>
                <li>📍 Tekirdağ/Çorlu</li>
              </ul>
              <div className="footer-social">
                <a href="https://www.instagram.com/digitalentrasoftware/" target="_blank" rel="noopener noreferrer" className="social-link">
                  Instagram
                </a>
                <a href="https://wa.me/905318287339" target="_blank" rel="noopener noreferrer" className="social-link social-whatsapp">
                  WhatsApp
                </a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2025 Digitalentra Software. Tüm hakları saklıdır.</p>
          </div>
        </div>
      </footer>

      {/* Floating Buttons */}
      <div className="floating-buttons">
        {/* WhatsApp Button */}
        <a 
          href="https://wa.me/905318287339" 
          target="_blank" 
          rel="noopener noreferrer"
          className="floating-btn whatsapp-btn"
          aria-label="WhatsApp"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
          </svg>
        </a>

        {/* Scroll to Top Button */}
        {showScrollTop && (
          <button 
            onClick={scrollToTop}
            className="floating-btn scroll-top-btn"
            aria-label="Scroll to top"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 19V5M5 12l7-7 7 7"/>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default HomePage;
